<script setup lang="ts">
/**
 * 底部状态栏：状态文本 + 文件操作 + 自动草稿提示。
 * 「打开工程」与「打开 SVG」是两个入口（工程文件会连视图态/锁定态一起恢复）。
 */
import { computed } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { IDLE_STATUS, ui } from '@/stores/editor';
import ExportPanel from './ExportPanel.vue';
import OpenFileButton from './OpenFileButton.vue';

const editor = useEditor();

/** 空画板：默认那句「点击左侧列表或画布图元」此时是误导（根本没图元可点），换成能落地的说法 */
const shown = computed(() =>
  editor.blank.value && ui.status === IDLE_STATUS ? '空白画板 · 拖入 .svg 或点右下「打开 SVG」' : ui.status,
);

const draftWhen = computed(() => {
  const draft = editor.draft.value;
  if (!draft) return '';
  const d = new Date(draft.savedAt);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
});

const restore = async (): Promise<void> => {
  await editor.restoreDraft();
};

const discard = async (): Promise<void> => {
  await editor.discardDraft();
};
</script>

<template>
  <footer class="bottom">
    <!-- 显示 ui.status：它是选中文案的唯一出口，也是各处错误提示的出口（否则提示写完没人看见） -->
    <span class="status" data-field="status">{{ shown }}</span>
    <span class="grow" />

    <template v-if="!editor.pristine.value">
      <span class="status">已修改</span>
    </template>

    <template v-if="editor.draft.value">
      <span class="status draft-hint" data-field="draft-hint">发现未保存草稿（{{ draftWhen }}）</span>
      <button class="ghost-btn" data-field="draft-restore" @click="restore">恢复</button>
      <button class="ghost-btn" data-field="draft-discard" @click="discard">丢弃</button>
    </template>

    <button class="ghost-btn" title="新建空文档" @click="editor.newDocument()">新建</button>

    <OpenFileButton kind="svg" label="打开 SVG" class="ghost-btn" />
    <OpenFileButton kind="project" label="打开工程" class="ghost-btn" title="工程文件（含视图态 / 锁定态）" />

    <button class="ghost-btn" title="源码面板 Ctrl+E" @click="ui.codeOpen = !ui.codeOpen">Code</button>

    <!-- 所有导出收在这一处（SVG / PNG / JPEG / 工程），避免"导出"与"导出工程"并排却看不出区别 -->
    <button
      class="primary-btn"
      data-field="export-open"
      title="导出（Ctrl+S 直接导出 SVG）"
      :disabled="!editor.svg.value"
      @click="ui.exportOpen = !ui.exportOpen"
    >
      导出 ▾
    </button>
  </footer>

  <ExportPanel />
</template>
